import React, { useState } from "react";
import { ApplicationStatus } from "../../../app/enums/applicationStatus";
import { Button, Dropdown, Grid, Icon, Modal } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import GenderImage from "../../../components/GenderImage";
import ReflectionPreviewModal from "../../reflections/ReflectionPreviewModal";
import { Student } from "../../students/types";
import { Question, ReflectionFormModel } from "../../reflections/models";
import ReflectionWriteModal from "../../reflections/ReflectionWriteModal";
import { reflectionQuestions } from "../../reflections/reflection-questions";
import { saveApplicationFeedback } from "../../shadowings/actions";
import { isExistingReflection } from "../../../app/helpers/reflections";
import ShadowingMetadata from "../../../components/shadowing/ShadowingMetadata";
import { useLocation, useNavigate } from "react-router-dom";
import { Application } from "../../applications/types";
import GroupedFeedbackModal from "./GroupedFeedbackModal";
import { formatGrade } from "../../../app/helpers/gradeHelpers";
import { useAppDispatch } from "../../../app/hooks/useAppDispatch";
import agent from "../../../app/api/agent";
import useToaster from "../../../app/hooks/useToaster";
import { downloadFile } from "../../../app/helpers/fileHelper";

interface Props {
  shadowing: { applications: Application[] };
  openOnLoad?: boolean;
}

const studentQuestions: Question[] = [21, 22, 27, 28].map((x) => reflectionQuestions[x]);

const FeedbackListModal = ({ shadowing, openOnLoad = false }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(openOnLoad);
  const [applications, setApplications] = useState(
    shadowing.applications.filter((a) => a.status === ApplicationStatus.COMPANY_APPROVED)
  );
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { success } = useToaster();
  const [motivationLetter, setMotivationLetter] = useState<string | null>(null);

  const handleDownloadCv = async (cvAsset: string) => {
    const response = await agent.Files.getCv(cvAsset);
    downloadFile(response);
  };

  const handleSaveFeedbackAboutStudent = async (applicationId: number, value: ReflectionFormModel): Promise<any> => {
    await dispatch(saveApplicationFeedback(applicationId, value));
    setApplications((current) => current.map((a) => (a.id === applicationId ? { ...a, businessFeedback: value } : a)));
  };

  const handleNoShow = (applicationId: number) => {
    agent.Applications.noShow(applicationId).then(() => {
      setApplications(applications.map((a) => (a.id === applicationId ? { ...a, noShow: true } : a)));
      success(t("applications.notifications.no_show_marked"));
    });
  };

  const handleUndoNoShow = (applicationId: number) => {
    agent.Applications.noShowUndo(applicationId).then(() => {
      setApplications(applications.map((a) => (a.id === applicationId ? { ...a, noShow: false } : a)));
      success(t("applications.notifications.no_show_undone"));
    });
  };

  const onClose = () => {
    setOpen(false);
    if (location.pathname.startsWith("/open-feedback")) {
      navigate("/myPositions", { replace: true });
    }
  };

  const hasAnyFeedbackFromStudent = applications.some((application) => isExistingReflection(application.reflections));

  return (
    <>
      <Modal
        dimmer="inverted"
        closeOnEscape={true}
        closeOnDimmerClick={true}
        closeIcon
        onClose={onClose}
        onOpen={() => setOpen(true)}
        open={isOpen}
        trigger={<Button primary>{t("common.read_feedbacks")}</Button>}
      >
        <Modal.Header>{t("reflection.feedback_modal_title")}</Modal.Header>
        <Modal.Content>
          <Grid>
            {applications.map((application) => (
              <Grid.Row key={application.id} className="student__item">
                <Grid.Column width={2}>
                  <GenderImage gender={application.student.gender} />
                </Grid.Column>
                <Grid.Column width={14}>
                  <div className="student__name">
                    {application.student.contactInfo.fullName}{" "}
                    {formatGrade(application.student.grade, application.student.isStudent)}
                  </div>
                  <ShadowingMetadata
                    locationValue={application.student.school}
                    personValue={`${application.student.contactInfo.email}`}
                  />
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}
                  >
                    {application.cvAsset && (
                      <Button basic size="mini" onClick={() => handleDownloadCv(application.cvAsset)}>
                        <Icon name="file alternate outline" />
                        {t("applications.company.actions.download_cv")}
                      </Button>
                    )}
                    {application.motivationLetter && (
                      <Button basic size="mini" onClick={() => setMotivationLetter(application.motivationLetter)}>
                        <Icon name="envelope outline" />
                        {t("common.motivation_letter")}
                      </Button>
                    )}
                    {isExistingReflection(application.reflections) && (
                      <ReflectionPreviewModal
                        title={getStudentModalTitle(application.student)}
                        reflections={application.reflections}
                      >
                        <Button primary>{t("reflection.read_student_feedback")}</Button>
                      </ReflectionPreviewModal>
                    )}
                    {isExistingReflection(application.businessFeedback) ? (
                      <ReflectionPreviewModal
                        title="reflection.feedback_modal_title"
                        reflections={application.businessFeedback}
                      >
                        <Button basic>{t("reflection.read_writen_feedback")}</Button>
                      </ReflectionPreviewModal>
                    ) : (
                      <ReflectionWriteModal
                        title="reflection.feedback_modal_title"
                        questions={studentQuestions}
                        successMessage="reflection.feedback_success"
                        onSave={(value) => handleSaveFeedbackAboutStudent(application.id, value)}
                      >
                        <Button basic>{t("reflection.write_feedback")}</Button>
                      </ReflectionWriteModal>
                    )}
                    {application.status === ApplicationStatus.COMPANY_APPROVED && (
                      <Dropdown
                        icon={null}
                        style={{ margin: 0 }}
                        trigger={
                          <Button
                            basic
                            style={{
                              margin: 0,
                              color: application.noShow ? "#9f3a38" : undefined,
                              borderColor: application.noShow ? "#9f3a38" : undefined,
                            }}
                          >
                            <Icon
                              name={application.noShow ? "times circle" : "check circle"}
                              style={{ color: application.noShow ? "#9f3a38" : "#21ba45" }}
                            />
                            {application.noShow
                              ? t("applications.company.actions.no_show")
                              : t("applications.company.actions.no_show_undo")}
                            <Icon name="dropdown" />
                          </Button>
                        }
                        options={[
                          {
                            key: "attended",
                            text: t("applications.company.actions.no_show_undo"),
                            icon: { name: "check circle", color: "green" },
                            active: !application.noShow,
                            onClick: () => application.noShow && handleUndoNoShow(application.id),
                          },
                          {
                            key: "no-show",
                            text: t("applications.company.actions.no_show"),
                            icon: { name: "times circle", color: "red" },
                            active: application.noShow,
                            onClick: () => !application.noShow && handleNoShow(application.id),
                          },
                        ]}
                      />
                    )}
                  </div>
                </Grid.Column>
              </Grid.Row>
            ))}
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          {hasAnyFeedbackFromStudent && (
            <GroupedFeedbackModal applications={applications}>
              <Button primary>{t("reflection.read_all_student_feedbacks")}</Button>
            </GroupedFeedbackModal>
          )}
        </Modal.Actions>
      </Modal>

      <Modal
        dimmer="inverted"
        closeOnEscape={true}
        closeOnDimmerClick={true}
        closeIcon
        open={motivationLetter !== null}
        onClose={() => setMotivationLetter(null)}
      >
        <Modal.Header>{t("common.motivation_letter")}</Modal.Header>
        <Modal.Content>
          <p style={{ whiteSpace: "pre-wrap" }}>{motivationLetter}</p>
        </Modal.Content>
      </Modal>
    </>
  );
};

function getStudentModalTitle(student: Student): string {
  return `${student.contactInfo.fullName} ${formatGrade(student.grade, student.isStudent)}`;
}

export default FeedbackListModal;
